import { useRef, useState } from "react";
import { Camera, Loader2, Trash2, RefreshCw } from "lucide-react";
import { fileToCompressedDataURL } from "@/lib/image";

export default function ImageUploadField({
    value,
    onChange,
    label = "Foto",
    hint = "JPG, PNG o HEIC · se optimizará automáticamente.",
    testId,
    buttonLabel = "Subir foto desde el móvil",
    aspect = "16/10",
}) {
    const inputRef = useRef(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const open = () => {
        setError(null);
        inputRef.current?.click();
    };

    const handle = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setBusy(true);
        setError(null);
        try {
            const dataUrl = await fileToCompressedDataURL(file, {
                maxWidth: 1600,
                maxHeight: 1600,
                quality: 0.82,
                mime: file.type === "image/png" ? "image/png" : "image/jpeg",
            });
            onChange(dataUrl);
        } catch (err) {
            console.error(err);
            setError("No se pudo cargar la imagen. Prueba con otra.");
        } finally {
            setBusy(false);
            // Reset so picking the same file again still fires onChange
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    const clear = () => onChange("");

    return (
        <div>
            {label && <div className="label-soft">{label}</div>}

            <input
                ref={inputRef}
                data-testid={testId ? `${testId}-file` : undefined}
                type="file"
                accept="image/*"
                onChange={handle}
                className="hidden"
            />

            {value ? (
                <div
                    data-testid={testId ? `${testId}-preview` : undefined}
                    className="relative rounded-2xl overflow-hidden border border-[var(--line)] bg-[var(--bg-soft)]"
                    style={{ aspectRatio: aspect }}
                >
                    <img
                        src={value}
                        alt=""
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute bottom-3 left-3 right-3 flex items-center gap-2">
                        <button
                            type="button"
                            data-testid={testId ? `${testId}-change` : undefined}
                            onClick={open}
                            disabled={busy}
                            className="btn-ghost flex-1"
                            style={{
                                background: "rgba(255,255,255,0.92)",
                                backdropFilter: "blur(6px)",
                                padding: "10px 14px",
                                fontSize: 13,
                            }}
                        >
                            {busy ? (
                                <Loader2 size={14} className="animate-spin" />
                            ) : (
                                <RefreshCw size={14} />
                            )}
                            Cambiar foto
                        </button>
                        <button
                            type="button"
                            data-testid={testId ? `${testId}-remove` : undefined}
                            onClick={clear}
                            disabled={busy}
                            aria-label="Quitar foto"
                            className="w-10 h-10 rounded-full bg-white/92 backdrop-blur border border-[var(--line)] inline-flex items-center justify-center"
                            style={{
                                background: "rgba(255,255,255,0.92)",
                            }}
                        >
                            <Trash2 size={14} />
                        </button>
                    </div>
                </div>
            ) : (
                <button
                    type="button"
                    data-testid={testId}
                    onClick={open}
                    disabled={busy}
                    className="w-full rounded-2xl border-2 border-dashed border-[var(--line)] bg-white hover:border-[var(--gold-soft)] transition-colors px-5 py-7 flex flex-col items-center justify-center gap-2"
                    style={{ minHeight: 140 }}
                >
                    {busy ? (
                        <>
                            <Loader2
                                size={22}
                                className="animate-spin text-[var(--ink-soft)]"
                            />
                            <span className="text-sm text-[var(--ink-soft)]">
                                Optimizando imagen…
                            </span>
                        </>
                    ) : (
                        <>
                            <span className="w-11 h-11 rounded-full bg-[var(--bg-soft)] flex items-center justify-center">
                                <Camera size={18} />
                            </span>
                            <span className="text-sm font-medium">
                                {buttonLabel}
                            </span>
                        </>
                    )}
                </button>
            )}

            {hint && !error && (
                <p className="text-xs text-[var(--ink-soft)] mt-2">{hint}</p>
            )}
            {error && <p className="hint-error">{error}</p>}
        </div>
    );
}
